import queryString from 'query-string';
import { getDataJoinedByComma, formatPrice } from '@utils/trackingUtils';
import { NOT_SET } from '@utils/tracking/constants';
import getShopStatus from '@utils/tracking/getShopStatus';
import { sortFoodCategory } from '@utils/shop';
import getShop from '@core/reducers/shop/selectors/getShop';
import getMenu from '@core/reducers/menu/selectors/getMenu';
import getHighlightedProducts from '@core/reducers/menu/selectors/getHighlightedProducts';
import { getDiscountsProducts } from '@core/reducers/menu/selectors/getDiscountsProducts';
import getPreferredDishesProducts from '@shopDetails/selectors/getPreferredDishesProducts';
import getSearchedLocationAreaId from '@location/selectors/getSearchedLocationAreaId';
import { getProductId } from '@productConfig/selectors/getProduct';
import { SHOP_IS_OPEN, SHOP_OPENIN, SHOP_IS_CLOSED } from '@shared/constants';
import { getRouterPrevLocation } from '@shared/selectors';
import getShopId from '@core/reducers/shop/selectors/getShopId';
import getShopName from '@core/reducers/shop/selectors/getShopName';
import getCapacityCheckData from '@shopDetails/selectors/getCapacityCheckData';
import getUseLiveOrderTracking from '@core/reducers/shop/selectors/getUseLiveOrderTracking';
import { DELIVERY_TYPE, TRACKING_ORIGIN } from '@commons/constants';
import {
  CAPACITY_CHECK,
  SHOP_NOTIFICATIONS,
  SWIMLANES,
} from '@shopDetails/constants';
import getShopBusinessType from '@core/reducers/shop/selectors/getShopBusinessType';
import { getShopAcceptsPreOrder } from '@conversion/selectors';
import getOriginFromQuery from '@shared/selectors/getOriginFromQuery';
import { track, logError } from '@tracking';
import getPaymentInfo from '@utils/tracking/getPaymentInfo';
import getMenuSections from '@shopDetails/selectors/getMenuSections';
import getListedInfoTrack from '@shopDetails/selectors/getListedInfoTrack';
import getShopDeliveryTime from '@utils/tracking/getShopDeliveryTime';
import getCartIsPickUp from '@core/reducers/cart/selectors/getCartIsPickUp';
import getCartDeliveryTimeExpected from '@core/reducers/cart/selectors/getCartDeliveryTimeExpected';
import getDistanceVendor from '@shopDetails/selectors/getDistanceVendor';
import { formatDistance } from '@shopDetails/utils/pickup';
import { getTrackingPlusValue } from '@shopDetails/utils/plus';
import getGlobalDiscountsWeekly from '@core/reducers/menu/selectors/getGlobalDiscountsWeekly';
import showSectionMenu from '@shopDetails/utils/menu';

const EVENT_NAME = 'shop_details.loaded';

function getItemsQuantity(menu) {
  return menu.sections.reduce(
    (acc, section) => section.products.length + acc,
    0,
  );
}

function getFeaturedProduct({ menu, fpClicked, areaId }) {
  if (!fpClicked) return null;
  const area = areaId ? `${areaId},` : '';
  if (!menu?.sections) return NOT_SET;
  const fp = menu.sections.map(section =>
    section.products.find(p => p.id === fpClicked),
  );
  return `${area}${fpClicked},${!!fp?.organic}`;
}

function getMainCuisine(categories) {
  if (!categories) return [];
  const food = categories.slice(categories.length - 3, categories.length);
  return food.map(category => category.name);
}

function buildNotifications(shop, capacityType) {
  let openStatus = '';
  if (shop?.opened === SHOP_OPENIN) {
    if (shop.acceptsPreOrder) openStatus = SHOP_NOTIFICATIONS.PREORDER;
    else openStatus = SHOP_NOTIFICATIONS.OPEN_LATER;
  }
  if (shop?.opened === SHOP_IS_CLOSED) {
    openStatus = SHOP_NOTIFICATIONS.CLOSED_TODAY;
  }
  const capacity =
    capacityType === CAPACITY_CHECK.NONE
      ? SHOP_NOTIFICATIONS.MOMENTARILY_CLOSED
      : '';
  if (openStatus || capacity) {
    return `${openStatus} ${capacity}`;
  }
  return NOT_SET;
}

function getSwimlanesShown(
  highlightProducts,
  discountsProducts,
  preferredDishesProducts,
) {
  const swimlanesList = [];
  if (preferredDishesProducts?.length > 0) {
    swimlanesList.push(SWIMLANES.PREFERRED_DISHES);
  }
  if (discountsProducts?.length > 0) {
    swimlanesList.push(SWIMLANES.DISCOUNTS);
  }
  if (highlightProducts?.length > 0) {
    swimlanesList.push(SWIMLANES.RECOMMENDED_PRODUCTS);
  }
  return swimlanesList.toString();
}

const commonResult = () => (dispatch, getState) => {
  const state = getState();
  const shop = getShop(state);
  const shopId = getShopId(state);
  const shopName = getShopName(state);
  const businessType = getShopBusinessType(state);
  const acceptsPreOrder = getShopAcceptsPreOrder(state);

  const menu = getMenu(state);
  const areaId = getSearchedLocationAreaId(state);
  const fpClicked = getProductId(state);
  const recommendedDishesQuantity = getHighlightedProducts(state);
  const discountsProductsQuantity = getDiscountsProducts(state);
  const preferredDishesProductsQuantity = getPreferredDishesProducts(state);
  const deeplink = null;
  const channel = null;
  const capacity = getCapacityCheckData(state);
  const capacityType = capacity?.capacity;
  const deliveryProvider = getUseLiveOrderTracking(state)
    ? DELIVERY_TYPE.WITH_LOGISITCS
    : DELIVERY_TYPE.OWN_DELIVERY;
  const menuSections = getMenuSections(state);
  const listedInfo = getListedInfoTrack(state);
  const distanceVendor = getDistanceVendor(state)?.distance;
  const deliveryTime = getCartDeliveryTimeExpected(state);
  const orderPickup = Boolean(getCartIsPickUp(state));
  const loyalty = getTrackingPlusValue(state);
  const weeklyDiscount = getGlobalDiscountsWeekly(state);

  return {
    shopId,
    shopName,
    businessType,
    shopLayout: menu.layout,
    shopGoldVip: false, // TODO: Not implemented yet
    shopSponsoring: NOT_SET,
    shopDiscount: NOT_SET,
    shopClosePreorder: acceptsPreOrder && shop.opened !== SHOP_IS_OPEN,
    currencyCode: NOT_SET, // TODO: waiting to get this from IndexDb
    shopDeliveryFee: formatPrice(shop.shippingAmount),
    shopMinimumOrderValue: formatPrice(shop.minDeliveryAmount),
    shopRatingQuantity: shop.generalScore,
    shopRatingQuality: shop.service,
    shopRecommendedDishesQuantity:
      (recommendedDishesQuantity && recommendedDishesQuantity.length) || 0,
    shopOpen: shop.opened === SHOP_IS_OPEN,
    shopStatus: getShopStatus(shop),
    shopSuggestedRestaurants: 0,
    featuredProduct: getFeaturedProduct({ menu, fpClicked, areaId }) || NOT_SET,
    shopHasVouchers: shop.acceptsVouchers,
    shopMainCuisine: getDataJoinedByComma(getMainCuisine(shop.categories)),
    restaurantDiscount: weeklyDiscount?.amount,
    origin: NOT_SET, // TODO: Not implemented yet
    cuisineMain: shop.categories?.sort(sortFoodCategory)[0]?.name,
    shopCategoryQuantity: shop.categories?.length,
    shopRatingSpeedQuality: shop.speed,
    shopRatingFoodQuality: shop.food,
    shopRatingServiceQuality: shop.service,
    shopIsFavorite: shop.favoriteByUser,
    shopDeliveryTime: getShopDeliveryTime({
      isPickup: orderPickup,
      deliveryTime,
    }),
    shopPaymentMethods: getPaymentInfo(shop.paymentMethods),
    shopChannels: NOT_SET, // TODO: waiting to get this from IndexDb
    shopItemsQuantity: getItemsQuantity(menu),
    deeplink,
    channel,
    deliveryProvider,
    shopProductLimitQuantity: shop?.maxProductQuantity,
    shopDetailsNotifications: buildNotifications(shop, capacityType),
    menuSectionShown: showSectionMenu(menuSections),
    swimlaneTitleList:
      getSwimlanesShown(
        recommendedDishesQuantity,
        discountsProductsQuantity,
        preferredDishesProductsQuantity,
      ) || NOT_SET,
    listedInfo,
    orderPickup,
    shopDistance: distanceVendor ? formatDistance(distanceVendor) : NOT_SET,
    loyalty,
  };
};

export const trackShopDetailsLoaded = () => (dispatch, getState) => {
  try {
    const state = getState();
    const origin = getOriginFromQuery(state);
    const payload = dispatch(commonResult());
    const prevLocation = getRouterPrevLocation(state);
    if (origin === TRACKING_ORIGIN.CHANNEL_LANDING) {
      if (prevLocation) {
        payload.deeplink = `${prevLocation.pathname}${prevLocation.search}`;
        payload.channel = queryString.parse(prevLocation.search).channels;
      }
      payload.origin = TRACKING_ORIGIN.CHANNEL_LANDING;
    }
    track(EVENT_NAME, payload);
  } catch (error) {
    logError(EVENT_NAME, error);
  }
};
